import { useQuery } from "@tanstack/react-query";
import { ChevronRight, Search, Users } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/layout";
import { Pagination } from "../components/pagination";
import type { AccountStatus, AdminAccountsQuery } from "../lib/api";
import { adminApi } from "../lib/api";
import { useAdminAuth } from "../lib/auth";
import { formatDateTime, formatMoscowDateTime, formatTokens, formatUsd } from "../lib/format";

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

const statuses: AccountStatus[] = ["ACTIVE", "SUSPENDED"];

function isAccountStatus(value: string): value is AccountStatus {
  return statuses.some((status) => status === value);
}

function AccountStatusBadge({ status }: { status: AccountStatus }) {
  const suspended = status === "SUSPENDED";
  return (
    <span
      className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium ${
        suspended
          ? "border-danger/25 bg-danger/10 text-danger"
          : "border-primary/25 bg-primary/10 text-primary"
      }`}
    >
      {suspended ? "Приостановлен" : "Активен"}
    </span>
  );
}

export function AccountsPage() {
  const { token } = useAdminAuth();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [tariffId, setTariffId] = useState("");
  const [status, setStatus] = useState<AccountStatus | "">("");
  const [cursorStack, setCursorStack] = useState<Array<string | null>>([null]);
  const [pageIndex, setPageIndex] = useState(0);

  function resetPagination() {
    setCursorStack([null]);
    setPageIndex(0);
  }

  useEffect(() => {
    const normalizedSearch = search.trim();
    if (normalizedSearch === debouncedSearch) return;

    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(normalizedSearch);
      resetPagination();
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [debouncedSearch, search]);

  const currentCursor = cursorStack[pageIndex] ?? null;
  const query = useMemo<AdminAccountsQuery>(
    () => ({
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
      ...(tariffId ? { tariffId } : {}),
      ...(status ? { status } : {}),
      ...(currentCursor ? { cursor: currentCursor } : {}),
      take: PAGE_SIZE,
    }),
    [currentCursor, debouncedSearch, tariffId, status],
  );

  const catalog = useQuery({
    queryKey: ["admin", "tariffs"],
    queryFn: () => adminApi.tariffs(token!),
    enabled: Boolean(token),
  });
  const tariffOptions = catalog.data
    ? [catalog.data.definition.freemium, catalog.data.definition.trial, ...catalog.data.definition.paidTariffs]
    : [];
  const selectedTariffExists = tariffOptions.some((item) => item.id === tariffId);

  const accounts = useQuery({
    queryKey: ["admin", "accounts", query],
    queryFn: () => adminApi.accounts(token!, query),
    enabled: Boolean(token),
    staleTime: 15_000,
  });

  function handleTariffChange(value: string) {
    setTariffId(value);
    resetPagination();
  }

  function handleStatusChange(value: string) {
    setStatus(isAccountStatus(value) ? value : "");
    resetPagination();
  }

  function handleForward() {
    const nextCursor = accounts.data?.nextCursor;
    if (!nextCursor) return;

    setCursorStack((current) => [
      ...current.slice(0, pageIndex + 1),
      nextCursor,
    ]);
    setPageIndex((current) => current + 1);
  }

  return (
    <div>
      <PageHeader
        title="Клиенты"
        description="Аккаунты, пользователи, квота диалогов и последние запуски пайплайна."
      />
      <p className="mb-4 text-sm text-muted-foreground">
        LLM-токены и стоимость — только для входящей обработки.
        Сгенерированные касания и аналитические отчёты не включены.
      </p>

      <section className="panel mb-4 grid gap-3 p-4 md:grid-cols-[minmax(0,1fr)_180px_180px]">
        <label className="block text-sm font-medium">
          <span className="mb-1.5 block">Поиск</span>
          <span className="relative block">
            <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <input
              className="control pl-9"
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Email, телефон или ID amoCRM"
            />
          </span>
        </label>
        <label className="block text-sm font-medium">
          <span className="mb-1.5 block">Тариф</span>
          <select
            className="control"
            value={tariffId}
            disabled={catalog.isError || !catalog.data}
            onChange={(event) => handleTariffChange(event.target.value)}
          >
            <option value="">Все тарифы</option>
            {tariffId && !selectedTariffExists ? (
              <option value={tariffId} disabled>{tariffId} — недоступен в каталоге</option>
            ) : null}
            {tariffOptions.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name} ({item.id})
              </option>
            ))}
          </select>
        </label>
        <label className="block text-sm font-medium">
          <span className="mb-1.5 block">Статус</span>
          <select
            className="control"
            value={status}
            onChange={(event) => handleStatusChange(event.target.value)}
          >
            <option value="">Все статусы</option>
            <option value="ACTIVE">Активные</option>
            <option value="SUSPENDED">Приостановленные</option>
          </select>
        </label>
      </section>

      {catalog.isError ? (
        <div className="mb-4 flex items-center gap-3 text-sm text-danger" role="alert">
          Каталог тарифов недоступен, фильтр временно отключён.
          <button type="button" className="underline" onClick={() => void catalog.refetch()}>
            Повторить загрузку каталога
          </button>
        </div>
      ) : null}

      {accounts.isLoading ? (
        <div className="panel p-4 text-sm text-muted-foreground">
          Загружаем аккаунты...
        </div>
      ) : accounts.error ? (
        <div className="panel p-4 text-sm text-danger">
          {accounts.error.message}
        </div>
      ) : accounts.data?.items.length === 0 ? (
        <div className="panel flex items-center gap-3 p-4 text-sm text-muted-foreground">
          <Users className="h-5 w-5" />
          Аккаунты не найдены.
        </div>
      ) : (
        <div className="space-y-2">
          {(accounts.data?.items ?? []).map((account) => (
            <Link
              key={account.id}
              className="panel block p-4 transition-colors hover:border-primary/40 hover:bg-accent/40"
              to={`/accounts/${account.id}`}
            >
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="truncate text-base font-semibold">
                      {account.amoAccountId}
                    </p>
                    <AccountStatusBadge status={account.status} />
                  </div>
                  <p className="mt-1 truncate text-sm text-muted-foreground">
                    {account.user?.email ?? "Пользователь не привязан"}
                  </p>
                  {account.user ? (
                    <p className="mt-1 truncate text-sm text-muted-foreground">
                      {account.user.phone}
                    </p>
                  ) : null}
                </div>

                <div className="grid min-w-0 grid-cols-2 gap-2 text-sm sm:w-[720px] sm:grid-cols-5">
                  <div>
                    <p className="text-muted-foreground">Тариф</p>
                    <p className="font-medium">{account.tariff.name}</p>
                    <p className="text-xs text-muted-foreground">{account.tariff.id}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Агенты</p>
                    <p className="font-medium">
                      {formatTokens(account.funnelsCount)}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Диалоги</p>
                    <p className="font-medium">Использовано: {formatTokens(account.billingSummary.usedDialogs)}</p>
                    <p className="text-xs text-muted-foreground">{account.billingSummary.quotaWindow === "TRIAL_TOTAL" ? "Общий запас на пробный период" : "Месячный объём"}: {formatTokens(account.billingSummary.monthlyDialogs)}</p>
                    <p className="text-xs text-muted-foreground">Доступно: {formatTokens(account.billingSummary.availableDialogs)}</p>
                    <p className="text-xs text-muted-foreground">Ожидает подтверждения: {formatTokens(account.billingSummary.reservedDialogs)}</p>
                    <p className="text-xs text-muted-foreground">{account.billingSummary.quotaWindow === "TRIAL_TOTAL" ? "Конец пробного периода" : "Обновление"}: {formatMoscowDateTime(account.billingSummary.periodEndsAt)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">LLM входящих</p>
                    <p className="font-medium">
                      {formatTokens(account.llmUsage.totalTokens)} ток.
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatUsd(account.llmUsage.costUsd)}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Последний run</p>
                    <p className="font-medium">
                      {formatDateTime(account.lastRunAt, "нет запусков")}
                    </p>
                  </div>
                </div>

                <ChevronRight className="hidden h-5 w-5 shrink-0 text-muted-foreground sm:block" />
              </div>
            </Link>
          ))}
        </div>
      )}

      <Pagination
        canGoBack={pageIndex > 0}
        canGoForward={Boolean(accounts.data?.nextCursor)}
        onBack={() => setPageIndex((current) => Math.max(0, current - 1))}
        onForward={handleForward}
        isLoading={accounts.isFetching}
      />
    </div>
  );
}
